import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { PlatformAppContext } from '@/app/platform/platform-context/platform-app-context';
import { map, share } from 'rxjs/operators';
import { USER_QUERIES, UserQueries } from '@/app/platform/platform-core/user/store/types';
import { FeatureConfig, PageFeature } from '@ervo/page-framework';

@Injectable({
  providedIn: 'root'
})
/**
 * The context for the current NavigationItem
 */
export class PlatformNavigationContext {
  constructor(
    private readonly egiAppContext: PlatformAppContext,
    @Inject(USER_QUERIES)
    private readonly userQueries: UserQueries
  ) {}

  /**
   * Whether the given feature is allowed for this user on this route.
   * @param name - The name of the feature
   */
  isFeatureAllowed$(name: string): Observable<boolean> {
    return this.featureForbiddenReasons$(name).pipe(map(reasons => reasons.length === 0), share());
  }

  isFeatureForbidden$(name: string): Observable<boolean> {
    return this.isFeatureAllowed$(name).pipe(map(isAllowed => !isAllowed));
  }

  featureForbiddenReasons$(name: string): Observable<FeatureForbiddenReason[]> {
    return this.egiAppContext.activeNavigationItem$.pipe(
      map(navigationItem => {
        const reasons: FeatureForbiddenReason[] = [];
        if (!navigationItem || !navigationItem.hasEgiPage) {
          reasons.push(FeatureForbiddenReason.unauthorized(name));
          return reasons;
        }

        const feature = navigationItem.egiPage.features.find(f => f.name === name);

        if (!feature) {
          reasons.push(FeatureForbiddenReason.unauthorized(name));
          return reasons;
        }

        const featureAllowedForUser = navigationItem.allowedFeaturesForUser.includes(
          feature.featureId
        );
        if (!featureAllowedForUser) {
          reasons.push(FeatureForbiddenReason.unauthorized(name));
        }

        if (!this.isFeatureAllowedForCurrentDeviceType(feature)) {
          reasons.push(FeatureForbiddenReason.incorrectDeviceType(feature.requiredDeviceTypes));
        }
        return reasons;
      }),
      share()
    );
  }

  /**
   * Whether or not the given clickTroughPage is allowed for this user on this route.
   * @param name - The name of the feature
   */
  isClickTroughPageAllowed$(name: string): Observable<boolean> {
    return this.egiAppContext.activeNavigationItem$.pipe(
      map(navItem => {
        if (!navItem) {
          return true;
        }

        const clickTroughPage = navItem.egiPage.config.clickTroughPages[name];
        return navItem.allowedFeaturesForUser.includes(clickTroughPage.id);
      })
    );
  }

  /**
   * Generates the router link for the given clickThroughPage including the correct appId and correct NavigationItemId
   * @param clickTroughName - The name of the clickTroughPage
   */
  getRouterLinkFor$(clickTroughName: string): Observable<EgiRouterLink> {
    return this.egiAppContext.activeNavigationItem$.pipe(
      map(navItem => {
        if (!navItem) {
          return defaultEgiRouterLink;
        }

        const config = navItem.egiPage.config;

        const clickTroughConfig = config.clickTroughPages;
        if (!config.clickTroughPages) {
          return defaultEgiRouterLink;
        }

        const id = this.extractClickTroughId(clickTroughConfig, clickTroughName);

        const clickTroughNavigationItem = navItem.children.find(c => c.egiPage.config.id === id);
        if (!clickTroughNavigationItem) {
          return defaultEgiRouterLink;
        }
        return {
          link: clickTroughNavigationItem.routerLink,
          queryParams: { navId: clickTroughNavigationItem.id }
        };
      })
    );
  }

  private isFeatureAllowedForCurrentDeviceType(feature: PageFeature): boolean {
    return !feature.requiredDeviceTypes?.length;
  }

  private extractClickTroughId(
    clickTroughConfig: { [p: string]: FeatureConfig },
    clickTroughName: string
  ) {
    const id = clickTroughConfig[clickTroughName]?.id;
    return id;
  }
}

export interface EgiRouterLink {
  link: string;
  queryParams: { navId: string };
}

const defaultEgiRouterLink: EgiRouterLink = {
  link: '',
  queryParams: { navId: '' }
};

export class FeatureForbiddenReason {
  private constructor(
    public readonly type: 'Unauthorized' | 'IncorrectDeviceType',
    private readonly requirements: string[]
  ) {}

  get requirementsList(): string {
    return this.requirements.join(', ');
  }

  public static unauthorized(featureName: string): FeatureForbiddenReason {
    return new FeatureForbiddenReason('Unauthorized', [featureName]);
  }

  public static incorrectDeviceType(allowedDevices: string[]): FeatureForbiddenReason {
    return new FeatureForbiddenReason('IncorrectDeviceType', allowedDevices);
  }
}
